import { Fragment, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import cn from 'classnames';

import { Checkbox } from '@wf/components';

import { type IAdditionalField, type IDBOptionAdditionalField } from '../../../License/types/request';
import { AdditionalFieldComparisonField } from '../ComparisonField/AdditionalFieldComparisonField';
import { licenseTypeAdditionalFieldsByTabsSelector } from '../../../License/store/selectors';
import { DataType, Tab } from '../../../License/types/enums';
import Separator from '../../../../components/Separator';

import { type TAdditionalFieldPathPrefix } from '../../types';

import { getAdditionalFieldIndexesById } from '../../helpers';
import { formatDate } from '../../../../helpers/dateHelpers';
import formatNumber from '../../../../helpers/formatNumber';

interface IAdditionalFieldsComparisonViewProps {
  tab: Tab;
  isOldVersion: boolean;
  differenceKey: 'leftValue' | 'rightValue';
  additionalFields: IAdditionalField[];
  dbOptionAdditionalFields: IDBOptionAdditionalField[];
  entityId: number;
}

function AdditionalFieldsComparisonView({
  tab,
  isOldVersion,
  differenceKey,
  additionalFields = [],
  dbOptionAdditionalFields = [],
  entityId,
}: IAdditionalFieldsComparisonViewProps) {
  const {
    i18n: { resolvedLanguage },
  } = useTranslation();
  const additionalFieldsByTab = useSelector(licenseTypeAdditionalFieldsByTabsSelector);
  const currentTabAdditionalFields = additionalFieldsByTab[tab];

  const additionalFieldIndexesById = useMemo(() => getAdditionalFieldIndexesById(additionalFields), [additionalFields]);

  const dbOptionAdditionalFieldIndexesById = useMemo(
    () => getAdditionalFieldIndexesById(dbOptionAdditionalFields),
    [dbOptionAdditionalFields]
  );

  if (!currentTabAdditionalFields?.length) {
    return null;
  }

  return (
    <>
      <Separator className="grid-full-width" />
      {currentTabAdditionalFields?.map((row) =>
        row.filter(Boolean).map((field, index) => {
          const { id, dataType } = field;
          const label = resolvedLanguage === 'en' ? field.englishLabel : field.nationalLanguageLabel;

          if (dataType === DataType.SEPARATOR) {
            return <Separator className="grid-full-width" key={id} />;
          }

          if (dataType === DataType.LABEL) {
            return (
              <h3 className="grid-full-width" key={id}>
                {label}
              </h3>
            );
          }

          const commonProps = {
            differenceKey,
            isOldVersion,
            label,
            className: cn({
              'grid-first-column': index === 0,
              'grid-full-width': field.fieldUILength && field.fieldUILength > 1,
            }),
          };

          if (dataType === DataType.OPTIONS_FROM_DATABASE) {
            const dbOptionFieldProps = {
              pathPrefix: (tab === Tab.ITEM
                ? `/items/${entityId}/dbOptionAdditionalFields`
                : '/dbOptionAdditionalFields') as TAdditionalFieldPathPrefix,
              index: dbOptionAdditionalFieldIndexesById[field.id] as number,
              values: dbOptionAdditionalFields,
            };

            const label2 = resolvedLanguage === 'en' ? field.name2Label : field.name2NationalLanguageLabel;
            const label3 = resolvedLanguage === 'en' ? field.name3Label : field.name3NationalLanguageLabel;

            return (
              <Fragment key={id}>
                <AdditionalFieldComparisonField {...commonProps} {...dbOptionFieldProps} fieldKey="fieldCode" />
                {field.name2Label && (
                  <AdditionalFieldComparisonField
                    {...commonProps}
                    {...dbOptionFieldProps}
                    className={field.field2UILength && field.field2UILength > 1 ? 'grid-full-width' : undefined}
                    label={label2 as string}
                    fieldKey="fieldValue1"
                  />
                )}
                {field.name3Label && (
                  <AdditionalFieldComparisonField
                    {...commonProps}
                    {...dbOptionFieldProps}
                    className={field.field3UILength && field.field3UILength > 1 ? 'grid-full-width' : undefined}
                    label={label3 as string}
                    fieldKey="fieldValue2"
                  />
                )}
              </Fragment>
            );
          }

          if (dataType === DataType.CHECKBOX) {
            return (
              <AdditionalFieldComparisonField
                {...commonProps}
                renderComponent={({ value, valueHasChanged, isOldVersion }) => (
                  <Checkbox
                    versionColor={valueHasChanged ? (isOldVersion ? 'error' : 'success') : undefined}
                    checked={value === 'true'}
                    disabled
                  />
                )}
                pathPrefix={tab === Tab.ITEM ? `/items/${entityId}/additionalFields` : '/additionalFields'}
                index={additionalFieldIndexesById[field.id] as number}
                values={additionalFields}
                fieldKey="fieldValue"
                key={id}
              />
            );
          }

          const render = (value: string) => {
            if (field.dataType === DataType.DECIMAL) {
              return formatNumber({
                value,
                minimumFractionDigits: field.scale,
                maximumFractionDigits: field.scale,
                locale: resolvedLanguage,
              }) as string;
            }

            if (field.dataType === DataType.DATE) {
              return formatDate(value);
            }

            return value;
          };

          return (
            <AdditionalFieldComparisonField
              {...commonProps}
              pathPrefix={tab === Tab.ITEM ? `/items/${entityId}/additionalFields` : '/additionalFields'}
              index={additionalFieldIndexesById[field.id] as number}
              values={additionalFields}
              fieldKey="fieldValue"
              render={render}
              key={id}
            />
          );
        })
      )}
    </>
  );
}

export default AdditionalFieldsComparisonView;
